/**
 * Custom Discoverability Layer
 *
 * Quiet selector-backed discovery markers for admin-authored campaigns.
 *
 * Primary responsibilities:
 * - Attach custom feature prompts to captured UI selectors
 * - Record impressions, clicks, and dismissals without interrupting work
 *
 * Dependencies:
 * - Discoverability context
 * - Custom feature catalog API
 */

'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useRouter } from 'nextjs-toploader/app'
import { ArrowRightIcon, Cross2Icon } from '@radix-ui/react-icons'

import { Button } from '@/components/ui/button'
import {
  canUseDiscoverabilityFeature,
  DISCOVERABILITY_MAX_SESSION_PROMPTS,
  DISCOVERABILITY_PROMPT_COOLDOWN_DAYS,
  isDiscoverabilityDismissSnoozed
} from '@/lib/discoverability/getDiscoverabilityPrompts'
import { useDiscoverability } from '@/lib/discoverability/context'
import type { DiscoverabilityTrackedFeatureKey } from '@/lib/discoverability/features'
import type { DiscoverabilityCustomFeature } from '@/lib/discoverability/custom-features'

type MarkerPosition = {
  top: number
  left: number
}

const millisecondsPerDay = 24 * 60 * 60 * 1000
const DISCOVERY_IDLE_DELAY_MS = 2000

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

const isInputElement = (element: Element | null) => {
  if (!element) return false

  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    element.getAttribute('contenteditable') === 'true'
  )
}

const wasPromptedRecently = (lastPromptedAt: string | null | undefined) => {
  if (!lastPromptedAt) return false

  const promptedAt = new Date(lastPromptedAt).getTime()
  if (!Number.isFinite(promptedAt)) return false

  return Date.now() - promptedAt < DISCOVERABILITY_PROMPT_COOLDOWN_DAYS * millisecondsPerDay
}

function queryTarget(selector: string) {
  try {
    return document.querySelector(selector)
  } catch {
    return null
  }
}

function toCustomFeatures(value: unknown): DiscoverabilityCustomFeature[] {
  if (!isRecord(value) || !Array.isArray(value.features)) return []

  return value.features.filter(isRecord) as DiscoverabilityCustomFeature[]
}

export function CustomDiscoverabilityLayer() {
  const router = useRouter()
  const pathname = usePathname()
  const discoverability = useDiscoverability()
  const [features, setFeatures] = useState<DiscoverabilityCustomFeature[]>([])
  const [target, setTarget] = useState<Element | null>(null)
  const [position, setPosition] = useState<MarkerPosition | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [dismissedFeatureKey, setDismissedFeatureKey] =
    useState<DiscoverabilityTrackedFeatureKey | null>(null)
  const [isUserInteracting, setIsUserInteracting] = useState(true)
  const impressionFeatureKeyRef = useRef<string | null>(null)

  useEffect(() => {
    let disposed = false

    fetch('/api/discoverability/custom-features', {
      cache: 'no-store',
      credentials: 'same-origin'
    })
      .then(async (response) => {
        if (!response.ok) return []

        const payload: unknown = await response.json()
        return toCustomFeatures(payload)
      })
      .then((nextFeatures) => {
        if (!disposed) {
          setFeatures(nextFeatures)
        }
      })
      .catch(() => {
        if (!disposed) {
          setFeatures([])
        }
      })

    return () => {
      disposed = true
    }
  }, [])

  useEffect(() => {
    let timeoutId: ReturnType<typeof window.setTimeout> | null = null

    const markInteracting = () => {
      setIsUserInteracting(true)

      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }

      timeoutId = window.setTimeout(() => {
        setIsUserInteracting(false)
      }, DISCOVERY_IDLE_DELAY_MS)
    }

    const events = ['keydown', 'pointerdown', 'input', 'scroll', 'wheel'] as const
    const options: AddEventListenerOptions = { capture: true, passive: true }

    markInteracting()
    events.forEach((eventName) => {
      window.addEventListener(eventName, markInteracting, options)
    })

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }

      events.forEach((eventName) => {
        window.removeEventListener(eventName, markInteracting, options)
      })
    }
  }, [])

  const activeFeature = useMemo(() => {
    if (typeof document === 'undefined') return null
    if (!discoverability.ready) return null
    if (discoverability.isTourActive) return null
    if (isUserInteracting) return null
    if (discoverability.sessionPromptCount >= DISCOVERABILITY_MAX_SESSION_PROMPTS) return null
    if (isInputElement(document.activeElement)) return null

    return (
      features.find((feature) => {
        if (!feature.enabled) return false
        if (!canUseDiscoverabilityFeature(discoverability.userPlan, feature.minimumPlan)) return false
        if (feature.routeHint && !pathname?.startsWith(feature.routeHint)) return false
        if (dismissedFeatureKey === feature.featureKey) return false

        const usage = discoverability.usage[feature.featureKey]
        if (usage?.firstUsedAt || (usage?.useCount ?? 0) > 0) return false
        if (usage?.completedOnboardingAt) return false
        if (isDiscoverabilityDismissSnoozed(usage)) return false
        if ((usage?.dismissedPromptCount ?? 0) <= 0 && wasPromptedRecently(usage?.lastPromptedAt)) {
          return false
        }

        return Boolean(queryTarget(feature.finalSelector))
      }) ?? null
    )
  }, [
    discoverability.isTourActive,
    discoverability.ready,
    discoverability.sessionPromptCount,
    discoverability.usage,
    discoverability.userPlan,
    dismissedFeatureKey,
    features,
    isUserInteracting,
    pathname
  ])

  const updatePosition = useCallback(() => {
    if (!activeFeature) {
      setTarget(null)
      setPosition(null)
      return
    }

    const nextTarget = queryTarget(activeFeature.finalSelector)
    if (!nextTarget) {
      setTarget(null)
      setPosition(null)
      return
    }

    const rect = nextTarget.getBoundingClientRect()
    setTarget(nextTarget)
    setPosition({
      top: Math.max(12, rect.top - 8),
      left: Math.min(window.innerWidth - 112, Math.max(12, rect.right + 8))
    })
  }, [activeFeature])

  useEffect(() => {
    updatePosition()

    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [updatePosition])

  useEffect(() => {
    if (
      !activeFeature ||
      !target ||
      impressionFeatureKeyRef.current === activeFeature.featureKey
    ) {
      return
    }

    impressionFeatureKeyRef.current = activeFeature.featureKey
    void discoverability.recordEvent({
      featureKey: activeFeature.featureKey,
      eventType: 'tooltip_shown',
      surface: activeFeature.surface,
      trigger: activeFeature.trigger || 'custom_selector',
      metadata: {
        kind: activeFeature.kind,
        selector: activeFeature.finalSelector,
        routeHint: activeFeature.routeHint
      }
    })
  }, [activeFeature, discoverability, target])

  if (!activeFeature || !position || !target) {
    return null
  }

  const dismissFeature = () => {
    setDismissedFeatureKey(activeFeature.featureKey)
    setExpanded(false)
    void discoverability.recordEvent({
      featureKey: activeFeature.featureKey,
      eventType: 'tooltip_dismissed',
      surface: activeFeature.surface,
      trigger: activeFeature.trigger || 'custom_selector',
      metadata: { kind: activeFeature.kind, selector: activeFeature.finalSelector }
    })
  }

  const openFeature = () => {
    void discoverability.recordEvent({
      featureKey: activeFeature.featureKey,
      eventType: 'tooltip_clicked',
      surface: activeFeature.surface,
      trigger: activeFeature.trigger || 'custom_selector',
      metadata: { kind: activeFeature.kind, selector: activeFeature.finalSelector }
    })
    router.push(activeFeature.ctaHref)
  }

  return (
    <div
      className="pointer-events-none fixed z-40"
      style={{
        top: position.top,
        left: position.left
      }}
    >
      <div className="pointer-events-auto w-72 max-w-[calc(100vw-1.5rem)]">
        {!expanded ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="bg-background text-foreground border-border max-w-full truncate rounded-md border px-3 py-1.5 text-xs font-medium shadow-sm transition-[transform,box-shadow] active:scale-[0.98]"
          >
            {activeFeature.title}
          </button>
        ) : (
          <section
            className="bg-background rounded-lg border p-3 shadow-lg"
            aria-label={activeFeature.title}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold leading-5">{activeFeature.title}</h2>
                <p className="text-muted-foreground mt-1 text-xs leading-5">
                  {activeFeature.body}
                </p>
              </div>
              <button
                type="button"
                aria-label="Dismiss custom discovery prompt"
                onClick={dismissFeature}
                className="text-muted-foreground hover:bg-muted hover:text-foreground -mr-1 -mt-1 inline-flex size-7 shrink-0 items-center justify-center rounded-md transition-colors"
              >
                <Cross2Icon className="size-4" aria-hidden="true" />
              </button>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button size="xs" onClick={openFeature}>
                {activeFeature.ctaLabel}
                <ArrowRightIcon className="size-3.5" aria-hidden="true" />
              </Button>
              <Button variant="ghost" size="xs" onClick={dismissFeature}>
                Later
              </Button>
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
